// Messages page — pure helpers (2026-07-12).
// The purchasing@ read-only monitor's client-side brain: row mappers,
// category vocabulary, list filtering, and (since 2026-07-18) the
// auto-link suggestion engine — sender-vendor matching + SKU/sidemark/
// item-name scanning, SUGGESTIONS ONLY, never silent (receipt-reader
// philosophy). Ingest happens server-side (api/inbound-email.js); the
// app only reads, marks, and links.
// Guard tests: src/__tests__/purchasingEmails.test.js +
// src/__tests__/emailLinkSuggestions.test.js.

import { resolveVendorByName, computeSidemark } from '../utils';

export var EMAIL_CATEGORY_META = [
  { key: 'order_confirmation', label: 'Order confirmation', color: '#1A5C38' },
  { key: 'shipping',           label: 'Shipping',           color: '#1A5C38' },
  { key: 'quote',              label: 'Quote',              color: '#D4007A' },
  { key: 'invoice_billing',    label: 'Invoice / billing',  color: '#D4007A' },
  { key: 'delay_issue',        label: 'Delay / issue',      color: '#B45309' },
  { key: 'marketing_spam',     label: 'Marketing',          color: '#888880' },
  { key: 'other',              label: 'Other',              color: '#888880' },
];

export function emailCategoryMeta(key) {
  return EMAIL_CATEGORY_META.find(function(c) { return c.key === key; })
    || { key: key || '', label: key || '', color: '#888880' };
}

export function rowToPurchasingEmail(row) {
  if (!row) return null;
  return {
    id:         row.id,
    messageId:  row.message_id  || '',
    fromEmail:  row.from_email  || '',
    fromName:   row.from_name   || '',
    toEmail:    row.to_email    || '',
    subject:    row.subject     || '',
    bodyText:   row.body_text   || '',
    bodyHtml:   row.body_html   || '',
    receivedAt: row.received_at || '',
    category:   row.category    || '',
    aiSummary:  row.ai_summary  || '',
    status:     row.status      || 'new',
    createdAt:  row.created_at  || '',
    dismissedItemIds: Array.isArray(row.dismissed_item_ids) ? row.dismissed_item_ids : [],
  };
}

export function rowToEmailAttachment(row) {
  if (!row) return null;
  return {
    id:          row.id,
    emailId:     row.email_id     || '',
    filename:    row.filename     || '',
    mimeType:    row.mime_type    || '',
    storagePath: row.storage_path || '',
    sizeBytes:   parseInt(row.size_bytes, 10) || 0,
  };
}

// Sender display: name when present, else the address's local part.
export function emailSenderLabel(e) {
  if (!e) return '';
  if (e.fromName) return e.fromName;
  var addr = String(e.fromEmail || '');
  return addr.indexOf('@') > 0 ? addr : (addr || '—');
}

// List filtering — status tab + free-text search across sender,
// subject, summary, and body. Dash/whitespace-normalized like the
// Reconciliation search.
export function filterPurchasingEmails(emails, opts) {
  var status = (opts && opts.status) || 'new';
  var q = String((opts && opts.q) || '').toLowerCase()
    .replace(/[‐-―−]/g, '-').replace(/\s+/g, ' ').trim();
  return (emails || []).filter(function(e) {
    if (!e) return false;
    if (status !== 'all' && e.status !== status) return false;
    if (!q) return true;
    var hay = [e.fromName, e.fromEmail, e.subject, e.aiSummary, e.category, e.bodyText]
      .join(' ').toLowerCase().replace(/[‐-―−]/g, '-').replace(/\s+/g, ' ');
    return hay.indexOf(q) >= 0;
  });
}

// Body for display: prefer the plain-text part; fall back to a
// tag-stripped version of the HTML.
export function emailDisplayBody(e) {
  if (!e) return '';
  if (e.bodyText && e.bodyText.trim()) return e.bodyText;
  return String(e.bodyHtml || '')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|tr|li|h[1-6])>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&#39;/g, "'").replace(/&quot;/g, '"')
    .replace(/[ \t]+/g, ' ').replace(/\n{3,}/g, '\n\n').trim();
}

// ── Auto-link suggestions (2026-07-18) ─────────────────────────────
// Pure matching, computed at view time against CURRENT items/vendors —
// nothing is stored except dismissals (purchasing_emails.
// dismissed_item_ids). Signals, strongest first:
//   1. The item's SKU or sidemark appears in the email text.
//   2. A PARTICIPANT vendor — the sender, the To: recipient, or an
//      address embedded in the body (forwarded "From:" headers, reply
//      quotes) resolves to a directory vendor — AND the item's name
//      appears in the text — or the vendor has so few open items the
//      list is worth showing on the vendor match alone. Covers the
//      CC-purchasing@-on-a-vendor-thread and forward-an-old-email
//      cataloging workflows (Cimon 2026-07-18): the sender is Cimon
//      in both, but the vendor is in the thread.
// Suggestions only — applying writes the junction row via the same
// linkItem path as the manual typeahead; dismissing persists per
// email + item so a dismissed suggestion never re-nags.

function squashText(s) {
  return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
}

// 'orders@hewn.com' → 'hewn'; 'quotes@mail.ogstudios.co.uk' is
// imperfect ('co') — the name/alias passes still get their shot.
export function emailSenderDomainBase(e) {
  var m = String((e && e.fromEmail) || '').match(/@([^@\s>]+)/);
  if (!m) return '';
  var parts = m[1].toLowerCase().split('.');
  return parts.length >= 2 ? parts[parts.length - 2] : parts[0];
}

// Every address that appears in the email — the sender, the To:
// recipient, and addresses embedded in the body (forwarded headers,
// "<x@y> wrote:" reply quotes, signatures). Own-firm addresses are
// dropped; capped so a mailing-list footer can't flood the matcher.
export function emailParticipantAddresses(e) {
  var out = [];
  function add(a) {
    a = String(a || '').toLowerCase().trim().replace(/[<>]/g, '');
    if (!a || a.indexOf('@') <= 0) return;
    if (a.indexOf('@cimonstarr.com') >= 0) return;
    if (out.indexOf(a) < 0) out.push(a);
  }
  if (!e) return out;
  add(e.fromEmail);
  add(e.toEmail);
  var body = ((e.bodyText || '') + ' ' + (e.bodyHtml || '')).slice(0, 20000);
  var found = body.match(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g) || [];
  for (var i = 0; i < found.length && out.length < 15; i++) add(found[i]);
  return out;
}

function domainBaseOfAddress(addr) {
  var host = (String(addr || '').split('@')[1] || '').toLowerCase().trim();
  if (!host) return '';
  var parts = host.split('.');
  return parts.length >= 2 ? parts[parts.length - 2] : parts[0];
}

function vendorByDomainBase(base, list) {
  if (!base || base.length < 3) return null;
  for (var k = 0; k < list.length; k++) {
    var v = list[k];
    if (!v || !v.name) continue;
    var keys = [v.name].concat(Array.isArray(v.aliases) ? v.aliases : []);
    for (var m2 = 0; m2 < keys.length; m2++) {
      var sq = squashText(keys[m2]);
      if (!sq || sq.length < 3) continue;
      if (sq === base || sq.indexOf(base) === 0 || base.indexOf(sq) === 0) return v;
    }
  }
  return null;
}

export function matchVendorForEmail(email, vendors) {
  if (!email) return null;
  var list = Array.isArray(vendors) ? vendors : [];
  if (!list.length) return null;

  // Pass 1 — the sender's full domain matches a vendor's email or any
  // contact's email domain.
  var fromDomain = (String(email.fromEmail || '').split('@')[1] || '').toLowerCase().trim();
  if (fromDomain) {
    for (var i = 0; i < list.length; i++) {
      var v = list[i];
      if (!v || !v.name) continue;
      var addrs = [v.email].concat((v.contacts || []).map(function(c) { return c && c.email; }));
      for (var j = 0; j < addrs.length; j++) {
        var d = (String(addrs[j] || '').split('@')[1] || '').toLowerCase().trim();
        if (d && d === fromDomain) return v;
      }
    }
  }

  // Pass 2 — the display name resolves through the canonical
  // name/alias resolver ("Hewn" <orders@...>).
  if (email.fromName) {
    var byName = resolveVendorByName(email.fromName, list);
    if (byName) return byName;
  }

  // Pass 3 — the sender domain's base label vs squashed vendor
  // name/aliases ('ogstudios' vs 'O+G Studios').
  var bySenderBase = vendorByDomainBase(emailSenderDomainBase(email), list);
  if (bySenderBase) return bySenderBase;

  // Pass 4 — OTHER participants (To: recipient, addresses embedded in
  // forwarded headers / reply quotes). Deliberately stricter than the
  // sender passes: an exact address match against the directory, or
  // the domain base vs vendor name/alias — NEVER bare domain equality,
  // so a stray someone@gmail.com in a signature can't claim a vendor
  // who happens to use gmail.
  var participants = emailParticipantAddresses(email);
  var directory = {};
  for (var p = 0; p < list.length; p++) {
    var v3 = list[p];
    if (!v3 || !v3.name) continue;
    [v3.email].concat((v3.contacts || []).map(function(c) { return c && c.email; }))
      .forEach(function(a) {
        a = String(a || '').toLowerCase().trim();
        if (a && !directory[a]) directory[a] = v3;
      });
  }
  for (var q = 0; q < participants.length; q++) {
    var addr = participants[q];
    if (directory[addr]) return directory[addr];
    var byBase = vendorByDomainBase(domainBaseOfAddress(addr), list);
    if (byBase) return byBase;
  }
  return null;
}

var SUGGEST_TERMINAL = { 'Cancelled': true, 'Complete': true };
var SUGGEST_CAP = 8;
var VENDOR_ONLY_CAP = 4;   // vendor match alone only when the open-item list is this short

export function suggestItemLinksForEmail(email, items, vendors, excludeIds) {
  if (!email) return [];
  var text = ' ' + ((email.subject || '') + ' ' + emailDisplayBody(email)).toLowerCase() + ' ';
  var squashed = squashText(text);
  var skip = {};
  (excludeIds || []).forEach(function(id) { skip[id] = true; });
  (email.dismissedItemIds || []).forEach(function(id) { skip[id] = true; });

  var out = [];
  function push(it, reason) {
    if (!it || !it.id || skip[it.id] || out.length >= SUGGEST_CAP) return;
    skip[it.id] = true;
    out.push({ item: it, reason: reason });
  }

  var list = (items || []).filter(function(it) { return it && it.id && !it.parentId; });

  // Signal 1 — SKU / sidemark in the text (vendor-independent).
  list.forEach(function(it) {
    var sku = String(it.itemNumber || '');
    if (sku.length >= 8 && squashed.indexOf(squashText(sku)) >= 0) {
      push(it, 'SKU ' + sku + ' appears in this email');
      return;
    }
    var sm = computeSidemark(it.project, it.roomName, it.roomNumber);
    if (sm && squashed.indexOf(squashText(sm)) >= 0) {
      push(it, 'Sidemark ' + sm + ' appears in this email');
    }
  });

  // Signal 2 — sender's vendor + item name in the text.
  var vendor = matchVendorForEmail(email, vendors);
  if (vendor) {
    var vendorItems = list.filter(function(it) { return it.vendor === vendor.name; });
    vendorItems.forEach(function(it) {
      var nm = String(it.item || it.productName || '').toLowerCase().trim();
      if (nm.length >= 4 && text.indexOf(nm) >= 0) {
        push(it, vendor.name + ' is on this email — item name matches');
      }
    });
    // Vendor match alone, only when their open-item list is short
    // enough to be signal rather than noise.
    if (!out.length) {
      var open = vendorItems.filter(function(it) { return !SUGGEST_TERMINAL[it.status]; });
      if (open.length && open.length <= VENDOR_ONLY_CAP) {
        open.forEach(function(it) { push(it, vendor.name + ' is on this email'); });
      }
    }
  }

  return out;
}

// Item picker: prefix-ish match on item name + SKU, capped for the
// dropdown. Excludes already-linked ids.
export function searchItemsForEmailLink(items, q, linkedIds, cap) {
  var query = String(q || '').toLowerCase().trim();
  if (!query) return [];
  var linked = {};
  (linkedIds || []).forEach(function(id) { linked[id] = true; });
  var max = cap || 8;
  var out = [];
  for (var i = 0; i < (items || []).length && out.length < max; i++) {
    var it = items[i];
    if (!it || !it.id || linked[it.id]) continue;
    var hay = ((it.item || '') + ' ' + (it.productName || '') + ' ' + (it.itemNumber || '')).toLowerCase();
    if (hay.indexOf(query) >= 0) out.push(it);
  }
  return out;
}
